import React from 'react';

import { ELMA365AppModelInput, ELMA365SourceParametersSettings } from '../../../../../../../gamma-api';
import ELMA365AppFieldsSelector from '../ELMA365AppFieldsSelector';
import ELMA365AppStatusSelector from '../ELMA365AppStatusSelector';
import { isAllowedForParsingField } from '../ELMA365AppFieldsSelector/utils';
import IbFormFieldLabel from '../../../../../../components/common/IbFormFieldLabel';
import { getTranslateFunc } from '../../../../../../utils/stringUtil';
import { BLOCK_GROUP_CLASS_NAME } from '../const';
import IbSwitch from '../../../../../../components/common/IbSwitch';
import IbTypography from '../../../../../../components/common/IbTypography';

export interface IParametersExtraProps {
  settings: ELMA365SourceParametersSettings;
  selectedApp?: ELMA365AppModelInput;
  loading?: boolean;
  onChange?: (settings: ELMA365SourceParametersSettings) => void;
}

const ParametersExtra: React.FC<IParametersExtraProps> = ({ settings, selectedApp, loading, onChange }) => {
  const translate = getTranslateFunc('Source settings: ELMA365:');

  const statusItems = selectedApp
    ? selectedApp.settings.status.items.map((item) => ({
        id: item.id,
        code: item.code,
        name: item.name,
        isSelected: settings.status?.selected_item_ids?.includes(item.id) ?? false,
      }))
    : [];

  const appFields = selectedApp
    ? selectedApp.fields.filter(isAllowedForParsingField).map((field) => ({
        code: field.code,
        name: field.view?.name ?? '',
        type: field.type,
        isSelected: settings.selected_field_codes?.includes(field.code) ?? false,
        contextElementField: field,
      }))
    : [];

  const onFeedEnabledChange = (value: string | boolean) => {
    onChange?.({
      ...settings,
      feed: {
        ...settings.feed,
        enabled: !!value,
      },
    });
  };

  const onStatusEnabledChange = (value: string | boolean) => {
    onChange?.({
      ...settings,
      status: {
        ...settings.status,
        refresh_enabled: !!value,
      },
    });
  };

  const onSelectedStatusChange = (selectedItemIds: number[]) => {
    onChange?.({
      ...settings,
      status: {
        ...settings.status,
        selected_item_ids: selectedItemIds,
      },
    });
  };

  const onSelectedFieldsChange = (selectedFieldsCodes: string[]) => {
    onChange?.({
      ...settings,
      selected_field_codes: selectedFieldsCodes,
    });
  };

  return (
    <>
      <div className={BLOCK_GROUP_CLASS_NAME}>
        <IbTypography>
          <IbSwitch checked={settings.feed.enabled} size="small" onChange={onFeedEnabledChange} />
          <IbTypography.Paragraph strong>{translate('Feed: Title')}</IbTypography.Paragraph>
        </IbTypography>
        <IbTypography>
          <IbTypography.Paragraph type="secondary">{translate('Feed: Description')}</IbTypography.Paragraph>
        </IbTypography>
      </div>
      <div className={BLOCK_GROUP_CLASS_NAME}>
        <IbTypography>
          <IbSwitch checked={settings.status?.refresh_enabled} size="small" onChange={onStatusEnabledChange} />
          <IbTypography.Paragraph strong>{translate('Status selector: Title')}</IbTypography.Paragraph>
        </IbTypography>
        <IbTypography>
          <IbTypography.Paragraph type="secondary">{translate('Status selector: Description')}</IbTypography.Paragraph>
        </IbTypography>
        {settings.status?.refresh_enabled && (
          <>
            <IbFormFieldLabel
              hint={translate('Status selector: Hint')}
              label={translate('Status selector: Subtitle')}
            />
            <ELMA365AppStatusSelector items={statusItems} loading={loading} onChange={onSelectedStatusChange} />
          </>
        )}
      </div>
      <div className={BLOCK_GROUP_CLASS_NAME}>
        <IbFormFieldLabel hint={translate('Fields selector: Hint')} label={translate('Fields selector: Subtitle')} />
        <ELMA365AppFieldsSelector fields={appFields} loading={loading} onChange={onSelectedFieldsChange} />
      </div>
    </>
  );
};

export default ParametersExtra;
